import { useEffect, useRef, useState } from 'react';
import Composer from './Composer.js';
import { hasAnswers } from './Context.js';
import { LINK, LinkStyles } from './Link.js';
import Preparing from './Preparing.js';
import Message from './Message.js';
import { connect, isVisible } from './Transport.js';
import { resolve } from './tokens.js';

/*
 * Chat
 *
 * The GRACI assistant, as a conversation.
 *
 * WHAT MAKES THIS DIFFERENT FROM A CHAT WIDGET is that it does not start empty.
 * It opens by sending the context the host handed it, so GRACI's first message
 * already accounts for everything the customer answered on the way here. An
 * assistant that opens with "how can I help?" after six questions is telling
 * the customer those six questions were wasted.
 *
 * The server owns the transcript. Every reply carries the whole conversation,
 * so local messages are optimistic only: they are drawn immediately for the
 * feel of it and thrown away as soon as the authoritative list arrives.
 *
 * Portability: see README.md. Nothing here imports from outside this folder.
 */
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const Chat = ({
  config,
  context,
  onClose,
  tokens: raw
}) => {
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState(null);
  const [id, setID] = useState(null);
  const [messages, setMessages] = useState([]);
  const [pending, setPending] = useState([]);
  const [text, setText] = useState('');
  const scroller = useRef(null);
  const transport = useRef(null);
  const tokens = resolve(raw);

  /*
   * Follow the conversation down as it grows, the way every chat does.
   *
   * By setting the scroll position on the list itself rather than calling
   * scrollIntoView on the last element. scrollIntoView walks up and scrolls
   * every ancestor that can move, including the page, which on a phone means
   * a chat message can scroll the whole document underneath the reader.
   */
  useEffect(() => {
    let node = scroller.current;
    if (node) {
      node.scrollTop = node.scrollHeight;
    }
  }, [messages, pending]);
  useEffect(() => {
    let live = true;
    transport.current = connect({
      auth: config?.auth,
      onError: message => {
        if (live) {
          setBusy(false);
          setError(message);
        }
      },
      onOutput: event => {
        if (!live) {
          return;
        }
        setBusy(false);
        setPending([]);
        setID(current => event.id || current);
        if (event.error) {
          setError(event.error);
          return;
        }
        setError(null);
        setMessages(event.messages.filter(isVisible));
      },
      url: config?.url
    });

    /*
     * The opening move is ours, not theirs. The server runs a completion
     * from the session variables alone, with no user message, which is what
     * produces a first reply that already knows the problem. The customer
     * sees the typing indicator, not a blank box waiting for them to start
     * again from nothing.
     */
    transport.current.send({
      session: context
    });
    return () => {
      live = false;
      transport.current?.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const onSend = () => {
    let content = text.trim();
    if (content.length === 0 || busy) {
      return;
    }
    setText('');
    setBusy(true);
    setPending([{
      content: content,
      role: 'user'
    }, {
      loading: true,
      role: 'assistant'
    }]);

    /*
     * `id` is what continues the thread. Given one the server loads the
     * stored history and discards the session, so the context above is sent
     * once and only once.
     */
    transport.current?.send({
      id: id,
      messages: [content],
      session: context
    });
  };
  let shown = messages.concat(pending);
  return /*#__PURE__*/_jsxs("div", {
    style: {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      minHeight: 0
    },
    children: [/*#__PURE__*/_jsxs("div", {
      ref: scroller,
      style: {
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        gap: 16,
        minHeight: 0,
        overflowY: 'auto',
        paddingBottom: 20
      },
      children: [shown.length === 0 && !error && /*#__PURE__*/_jsx(Preparing, {
        answered: hasAnswers(context),
        tokens: tokens
      }), shown.map((message, index) => /*#__PURE__*/_jsx(Message, {
        message: message,
        tokens: tokens
      }, index)), error && /*#__PURE__*/_jsx(Message, {
        message: {
          error: error,
          role: 'system'
        },
        tokens: tokens
      })]
    }), /*#__PURE__*/_jsxs("div", {
      style: {
        alignItems: 'center',
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
        paddingTop: 4
      },
      children: [/*#__PURE__*/_jsx(Composer, {
        busy: busy,
        disabled: Boolean(error) && shown.length === 0,
        onChange: setText,
        onSend: onSend,
        tokens: tokens,
        value: text
      }), /*#__PURE__*/_jsx("p", {
        style: {
          color: tokens.muted,
          fontFamily: tokens.font,
          fontSize: 12,
          lineHeight: 1.5,
          margin: '10px 2px 0',
          textAlign: 'center'
        },
        children: 'GRACI is an assistant and can be wrong. For anything urgent, or if a sensor is sounding, contact us directly.'
      }), onClose && /*#__PURE__*/_jsxs("button", {
        className: LINK,
        onClick: onClose,
        style: {
          background: 'none',
          border: 'none',
          color: tokens.accent,
          cursor: 'pointer',
          fontFamily: tokens.font,
          fontSize: 14,
          fontWeight: 600,
          marginLeft: -4,
          marginTop: 10,
          padding: '4px 4px'
        },
        children: [/*#__PURE__*/_jsx(LinkStyles, {}), 'I would rather talk to a person']
      })]
    })]
  });
};
export default Chat;